export type IncomingQuote = {
  quote_reference: string;
  form_type: 'free_quote' | 'quote_question' | 'request_service';
  received_at: string;
  name_declared: string;
  organization_declared: string;
  subject_declared: string | null;
  requested_area_declared: string | null;
};

export type IncomingQuotePage = {
  authority_organization_id: string;
  identity_id: string;
  source_namespace: 'profenz-public-website.service_requests.v1';
  source_revision: number;
  source_binding_fingerprint: string;
  items: IncomingQuote[];
  has_more: boolean;
  next_cursor: string | null;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const QUOTE = /^Q-[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const CURSOR = /^[A-Za-z0-9_-]{1,384}$/;
const FINGERPRINT = /^[0-9a-f]{64}$/;
const NAMESPACE = 'profenz-public-website.service_requests.v1';
const MAX_BYTES = 524288;
const FORMS = new Set(['free_quote', 'quote_question', 'request_service']);

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function summary(value: unknown): value is string | null {
  return value === null || (typeof value === 'string' && value.trim().length > 0 && value.length <= 300);
}

function unavailable(): Error {
  return new Error('Incoming quotes are unavailable.');
}

function timestamp(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) return false;
  const instant = new Date(value);
  return Number.isFinite(instant.getTime()) && instant.toISOString() === value;
}

// Bound bytes before JSON parsing, including unknown fields that must never reach the UI.
async function readBody(response: Response): Promise<unknown> {
  if (response.status !== 200 || response.headers.get('Content-Type')?.split(';')[0].trim().toLowerCase() !== 'application/json' || !response.body) throw unavailable();
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let length = 0;
  try {
    while (true) {
      const {done, value} = await reader.read();
      if (done) break;
      length += value.byteLength;
      if (length > MAX_BYTES) {
        await reader.cancel();
        throw unavailable();
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }
  const bytes = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
  try { return JSON.parse(new TextDecoder('utf-8', {fatal: true}).decode(bytes)) as unknown; }
  catch { throw unavailable(); }
}

export async function loadIncomingQuotes(
  authorityOrganizationId: string,
  identityId: string,
  cursor: string | null,
  signal: AbortSignal,
): Promise<IncomingQuotePage> {
  if (!UUID.test(authorityOrganizationId) || !UUID.test(identityId) || (cursor !== null && !CURSOR.test(cursor))) throw unavailable();
  const path = '/api/v1/internal/incoming-quotes' + (cursor === null ? '' : '?cursor=' + encodeURIComponent(cursor));
  const body = await readBody(await fetch(path, {
    method: 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error',
    headers: {Accept: 'application/json'}, signal,
  }));
  if (!record(body) || body.authority_organization_id !== authorityOrganizationId || body.identity_id !== identityId
    || body.source_namespace !== NAMESPACE
    || !Number.isInteger(body.source_revision) || Number(body.source_revision) < 1 || Number(body.source_revision) > 2147483647
    || typeof body.source_binding_fingerprint !== 'string' || !FINGERPRINT.test(body.source_binding_fingerprint)
    || !Array.isArray(body.items) || body.items.length > 50 || typeof body.has_more !== 'boolean'
    || !(body.next_cursor === null || (typeof body.next_cursor === 'string' && CURSOR.test(body.next_cursor)))
    || body.has_more !== (body.next_cursor !== null)
    || (body.has_more && body.items.length === 0)
    || (body.next_cursor !== null && body.next_cursor === cursor)) throw unavailable();
  const seen = new Set<string>();
  const items = body.items.map((value): IncomingQuote => {
    if (!record(value) || typeof value.quote_reference !== 'string' || !QUOTE.test(value.quote_reference) || seen.has(value.quote_reference)
      || typeof value.form_type !== 'string' || !FORMS.has(value.form_type) || !timestamp(value.received_at)
      || typeof value.name_declared !== 'string' || value.name_declared.trim().length === 0 || value.name_declared.length > 300
      || typeof value.organization_declared !== 'string' || value.organization_declared.length > 300
      || !summary(value.subject_declared) || !summary(value.requested_area_declared)) throw unavailable();
    seen.add(value.quote_reference);
    return {
      quote_reference: value.quote_reference, form_type: value.form_type as IncomingQuote['form_type'],
      received_at: value.received_at, name_declared: value.name_declared, organization_declared: value.organization_declared,
      subject_declared: value.subject_declared, requested_area_declared: value.requested_area_declared,
    };
  });
  return {
    authority_organization_id: authorityOrganizationId, identity_id: identityId, source_namespace: NAMESPACE,
    source_revision: Number(body.source_revision), source_binding_fingerprint: body.source_binding_fingerprint,
    items, has_more: body.has_more, next_cursor: body.next_cursor as string | null,
  };
}
